import { CONFIG } from '../core/config.mjs';
import { state, pictureElements } from '../core/state.mjs';
import { detectFormats } from '../detection/detect-formats.mjs';
import { normalizeImageSrc } from '../utils/image-utils.mjs';
import { getCachedContainerWidth } from '../utils/cache.mjs';

/**
 * Traite les éléments <picture ai-picture> pour ajouter des sources optimisées
 */
export async function pictureTransform() {
    if (CONFIG.debug) {
        console.log('[AI] Début transformation des pictures');
    }

    const formats = await detectFormats();
    const picturesWithAiPicture = pictureElements.filter(picture => picture.hasAttribute('ai-picture'));

    if (!picturesWithAiPicture.length) {
        if (CONFIG.debug) {
            console.log('[AI] Aucune picture[ai-picture] trouvée');
        }
        return;
    }

    for (const picture of picturesWithAiPicture) {
        try {
            await processPictureElement(picture, formats);
        } catch (error) {
            console.error('[AI] Erreur traitement picture:', error);
        }
    }

    if (CONFIG.debug) {
        console.log(`[AI] ${picturesWithAiPicture.length} pictures traitées`);
    }
}

async function processPictureElement(picture, formats) {
    // Trouver l'img à l'intérieur du picture
    const img = picture.querySelector('img');
    if (!img || !img.src) {
        if (CONFIG.debug) {
            console.log('[AI] Pas d\'img avec src trouvée dans picture');
        }
        return;
    }

    const originalSrc = img.src;

    // Vérifier si déjà traité
    if (picture.hasAttribute('ai-processed')) {
        return;
    }

    // Normaliser l'URL source
    const normalizedSrc = normalizeImageSrc(originalSrc);

    // Calculer la largeur du conteneur parent
    const containerWidth = getCachedContainerWidth(picture);

    // Déterminer le meilleur format disponible
    let targetFormat = '';
    if (CONFIG.formats.autoDetect) {
        if (formats.avif && CONFIG.formats.preferredFormat !== 'webp') {
            targetFormat = 'avif';
        } else if (formats.webp) {
            targetFormat = 'webp';
        }
    } else if (CONFIG.formats.preferredFormat && CONFIG.formats.preferredFormat !== 'auto') {
        targetFormat = CONFIG.formats.preferredFormat;
    }

    // Vérifier si les sources existent déjà pour éviter la duplication
    const existingSources = picture.querySelectorAll('source');
    const hasAvifSource = Array.from(existingSources).some(source => source.type === 'image/avif');
    const hasWebpSource = Array.from(existingSources).some(source => source.type === 'image/webp');

    // Ajouter les sources AVIF et WebP si elles n'existent pas déjà
    if (formats.avif && !hasAvifSource) {
        const avifSource = createSourceElement(originalSrc, 'avif', containerWidth);
        picture.insertBefore(avifSource, img);
    }

    if (formats.webp && !hasWebpSource) {
        const webpSource = createSourceElement(originalSrc, 'webp', containerWidth);
        picture.insertBefore(webpSource, img);
    }

    // Marquer comme traité
    picture.setAttribute('ai-processed', '1');

    // Stocker les métadonnées
    state.images.set(picture, {
        src: originalSrc,
        format: targetFormat,
        width: containerWidth,
        element: picture
    });

    if (CONFIG.debug) {
        console.log('[AI] Picture traitée:', {
            src: originalSrc,
            width: containerWidth,
            formats: { avif: formats.avif, webp: formats.webp }
        });
    }
}

function createSourceElement(src, format, width) {
    const source = document.createElement('source');

    // Construire l'URL pour l'image optimisée avec l'ancienne structure pour le JavaScript
    const imgEndpoint = '/img';
    const imageUrl = `${imgEndpoint}?image=${encodeURIComponent(src)}&format=${format}&width=${width}&org_if_sml=1`;

    source.srcset = imageUrl;
    source.type = `image/${format}`;

    return source;
}